import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { Delete, AddPhotoAlternate, Close } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import {
  fetchAllBanners,
  createBanner,
  toggleBannerStatus,
  deleteBanner,
} from "../../../State/admin/adminBannerSlice";
import { BannerType, HomeBanner } from "../../../types";
import { uploadToLocalhost } from "../../../config/uploadToLocalhost";

const BannersTable = () => {
  const dispatch = useAppDispatch();
  const { banners = [], loading = false } = useAppSelector(
    (state) => state.homeBanner || {}
  );

  const [open, setOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState<HomeBanner>({
    title: "",
    subtitle: "",
    imageUrl: "",
    targetUrl: "",
    promoCode: "",
    bannerType: BannerType.SLIDE,
  });

  useEffect(() => {
    dispatch(fetchAllBanners());
  }, [dispatch]);

  const handleToggle = (id?: number) => {
    if (id) dispatch(toggleBannerStatus(id));
  };

  const handleDelete = (id?: number) => {
    if (id) dispatch(deleteBanner(id));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const uploadedUrl = await uploadToLocalhost(file);
      if (uploadedUrl) {
        setFormData((prev) => ({ ...prev, imageUrl: uploadedUrl }));
      }
    } catch (error) {
      console.error("Error uploading banner image:", error);
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, imageUrl: "" }));
  };

  const handleSubmit = () => {
    if (!formData.imageUrl) {
      alert("Please upload a banner image before submitting.");
      return;
    }

    dispatch(createBanner(formData)).then(() => {
      setOpen(false);
      setFormData({
        title: "",
        subtitle: "",
        imageUrl: "",
        targetUrl: "",
        promoCode: "",
        bannerType: BannerType.SLIDE,
      });
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Custom Ads & Slides</h1>
        <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
          Create Banner / Slide
        </Button>
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead className="bg-slate-100">
            <TableRow>
              <TableCell>Preview</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Target Link</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {banners.map((banner: HomeBanner) => (
              <TableRow key={banner.id}>
                <TableCell>
                  <img
                    src={banner.imageUrl}
                    alt={banner.title}
                    className="w-16 h-10 object-cover rounded"
                  />
                </TableCell>
                <TableCell>{banner.title || "—"}</TableCell>
                <TableCell>
                  <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                    {banner.bannerType}
                  </span>
                </TableCell>
                <TableCell>{banner.targetUrl || "—"}</TableCell>
                <TableCell>
                  <Switch
                    checked={banner.active ?? false}
                    onChange={() => handleToggle(banner.id)}
                    color="primary"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => handleDelete(banner.id)} color="error">
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Modal */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add New Banner / Ad</DialogTitle>
        <DialogContent className="space-y-4 pt-4">
          <TextField
            select
            fullWidth
            label="Banner Placement Type"
            value={formData.bannerType}
            onChange={(e) =>
              setFormData({ ...formData, bannerType: e.target.value as BannerType })
            }
          >
            <MenuItem value={BannerType.SLIDE}>Home Hero Slide</MenuItem>
            <MenuItem value={BannerType.LEFT_AD}>Left Side Ad</MenuItem>
            <MenuItem value={BannerType.RIGHT_AD}>Right Side Ad</MenuItem>
          </TextField>

          {/* File Upload Control */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Banner Image</label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept="image/*"
                id="bannerFileInput"
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />

              {!formData.imageUrl ? (
                <label className="relative" htmlFor="bannerFileInput">
                  <span className="w-28 h-20 cursor-pointer flex items-center justify-center p-3 border-2 border-dashed rounded-md border-gray-400 hover:border-blue-500">
                    <AddPhotoAlternate className="text-gray-600" />
                  </span>
                  {uploadingImage && (
                    <div className="absolute inset-0 w-28 h-20 flex justify-center items-center bg-white/70 rounded-md">
                      <CircularProgress size={24} />
                    </div>
                  )}
                </label>
              ) : (
                <div className="relative w-28 h-20 border rounded-md overflow-hidden">
                  <img
                    className="w-full h-full object-cover"
                    src={formData.imageUrl}
                    alt="Uploaded Banner Preview"
                  />
                  <IconButton
                    onClick={handleRemoveImage}
                    size="small"
                    color="error"
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      backgroundColor: "rgba(255,255,255,0.8)",
                      "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
                    }}
                  >
                    <Close sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </div>
              )}
            </div>
          </div>

          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <TextField
            fullWidth
            label="Subtitle"
            value={formData.subtitle}
            onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
          />
          <TextField
            fullWidth
            label="Target URL (OnClick Redirection)"
            value={formData.targetUrl}
            onChange={(e) => setFormData({ ...formData, targetUrl: e.target.value })}
          />
          <TextField
            fullWidth
            label="Promo Code (Optional)"
            value={formData.promoCode}
            onChange={(e) => setFormData({ ...formData, promoCode: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={uploadingImage || !formData.imageUrl}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BannersTable;